import { trackEvent } from '@patrianna/analytics'
import { useDialogManager } from '@patrianna/dialog-manager'
import { useDeviceDetect, useIsSafari, usePrevious } from '@patrianna/shared-hooks'
import { useEffect } from 'react'

import usePWAInstance, { useIsPWAAvailable } from 'hooks/usePWA'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { togglePWAMessage } from 'store/modules/appConfig/actions'
import { getIsPWAMessageOpenSelector } from 'store/modules/appConfig/selectors'
import { CLOSE_AUTO_PWA_DIALOG, getItemFormLocalStorageSelector, setDataToLocalStorage } from 'utils/localStorage'

import Android from './components/Android'
import IOS from './components/IOS'
import MessageLayout from './components/MessageLayout'

function Root() {
  const isPWAMessageOpen = useAppSelector(getIsPWAMessageOpenSelector)
  const dispatch = useAppDispatch()
  const isAutoPWADialogClose = useAppSelector((state) =>
    getItemFormLocalStorageSelector(state, CLOSE_AUTO_PWA_DIALOG, false),
  )
  const pwaInstance = usePWAInstance()
  const deviceDetect = useDeviceDetect()
  const isIOS = useIsSafari()
  const isAndroid = deviceDetect.isAndroid()

  const isPwaAvailable = useIsPWAAvailable()
  const isPwaAvailableComputed = isPwaAvailable && !isAutoPWADialogClose && (isAndroid || isIOS)

  const activeDialogProps = useDialogManager('getActiveDialogProps')
  const needToHide = activeDialogProps?.dialogName === 'START_GAME_FLOW'

  const pwaMessageDisabled = !isPwaAvailable || !pwaInstance || !isPWAMessageOpen
  const pwaMessageActive = isPWAMessageOpen && !pwaMessageDisabled
  const prevPwaMessageActive = usePrevious(pwaMessageActive)

  const closeMessage = () => {
    dispatch(setDataToLocalStorage(CLOSE_AUTO_PWA_DIALOG, true, false))
    dispatch(togglePWAMessage({ isPWAMessageOpen: false }))
  }

  const closeMessageWithEvent = () => {
    closeMessage()
    trackEvent('pwa_popup_close_button_click')
  }

  const onTogglePWAMessageHandler = (data: { isPWAMessageOpen: boolean }) => {
    dispatch(togglePWAMessage(data))
  }

  const handleClick = () => {
    pwaInstance.prompt()
    closeMessage()
    trackEvent('pwa_popup_install_button_click')
  }

  useEffect(() => {
    const scrollHandler = () => {
      const isSystemBarVisible = document.getElementById('pwa-detection')?.offsetHeight > window.innerHeight

      onTogglePWAMessageHandler({ isPWAMessageOpen: isSystemBarVisible })
    }

    if (isPwaAvailableComputed && !needToHide) {
      window.addEventListener('scroll', scrollHandler)
    } else {
      onTogglePWAMessageHandler({ isPWAMessageOpen: false })
    }

    return () => {
      window.removeEventListener('scroll', scrollHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPwaAvailableComputed, needToHide])

  useEffect(() => {
    if (pwaMessageActive) {
      trackEvent('pwa_popup_appears')
    }
    if (prevPwaMessageActive && !pwaMessageActive) {
      trackEvent('pwa_popup_disappears')
    }
  }, [pwaMessageActive, prevPwaMessageActive])

  if (pwaMessageDisabled) {
    return null
  }

  if (isIOS) {
    return (
      <MessageLayout closeHandler={closeMessageWithEvent}>
        <IOS />
      </MessageLayout>
    )
  }

  if (isAndroid) {
    return (
      <MessageLayout closeHandler={closeMessageWithEvent} handleClick={handleClick} withButton>
        <Android />
      </MessageLayout>
    )
  }

  return null
}

export default Root
